import type { Profile, BandRole } from '../core/types';
import { ROLES, NM } from '../core/constants';
import { fmtNum, snap, stepFor } from '../core/util';
import { axPos } from '../core/wavelength';
import { wavelengthRGB } from '../core/wavelength';
import type { State, Store, Mode } from '../state/store';
import { INDICES } from '../render/indices';
import { RAMPS } from '../render/ramps';
import {
  canBalance, canClip, canLock, canTrueColour, compositeDefaults, indexExpr,
  isClassifying, physValue, samBands,
} from '../render/style';
import { autoIndexRange, classificationStats } from '../export/classification';
import { BandThumb, BandHistogram, BandStatsRow, IndexHistogram } from './Canvases';
import { ClassPanel } from './Classes';
import { CLASS_COLOURS } from '../render/sam';

interface PanelProps { store: Store; state: State; profile: Profile }

const MODES: [Mode, string][] = [
  ['index', 'Index'], ['rgb', 'True'], ['cir', 'CIR'], ['custom', 'Custom'], ['single', 'Band'],
];

/** Which indices can be picked right now, and why not. */
/** Header badge for the Bands section: how much of the naming came from the file. */
export function bandsBadge(p: Profile) {
  const named = p.bands.filter((b) => b.source === 'metadata').length;
  return { named, label: named ? `${named} named in file` : 'unnamed — check these' };
}

export function indexOptions(p: Profile, s: State) {
  return INDICES.map((ix) => {
    if (ix.sam) {
      if (samBands(p).length < 2) {
        return { id: ix.id, label: `${ix.name} — needs 2+ spectral bands`, disabled: true };
      }
      return { id: ix.id, label: ix.name, disabled: false };
    }
    if (ix.custom) return { id: ix.id, label: ix.name, disabled: false };
    const { error } = indexExpr(p, { ...s, indexId: ix.id });
    return { id: ix.id, label: error ? `${ix.name} — ${error}` : ix.name, disabled: !!error };
  });
}

export function RenderPanel({ store, state, profile }: PanelProps) {
  const opts = indexOptions(profile, state);
  const ix = INDICES.find((i) => i.id === state.indexId);
  return (
    <>
      <div class="seg">
        {MODES.map(([id, label]) => (
          <button key={id} class={state.mode === id ? 'on' : ''} onClick={() => {
            const patch: Partial<State> = { mode: id };
            if (id === 'rgb' || id === 'cir' || id === 'custom') {
              const [lo, hi] = compositeDefaults(profile, { ...state, mode: id });
              Object.assign(patch, { lo, hi });
            } else if (id === 'index' && ix) {
              // The data's own spread, falling back to the declared range.
              const auto = autoIndexRange(profile, { ...state, mode: 'index' });
              Object.assign(patch, auto
                ? { lo: auto[0], hi: auto[1] }
                : { lo: ix.range[0], hi: ix.range[1] });
            }
            else if (id === 'single') {
              const b = profile.bands[state.singleBand]!;
              Object.assign(patch, {
                lo: physValue(profile, b.index, b.stats.p2),
                hi: physValue(profile, b.index, b.stats.p98),
                rampId: b.role === 'thermal' ? 'magma' : state.rampId,
              });
            }
            store.set(patch);
          }}>{label}</button>
        ))}
      </div>

      {state.mode === 'index' && (
        <>
          <div class="row"><label>index</label>
            <select data-testid="index" value={state.indexId} onChange={(e) => {
              const id = (e.target as HTMLSelectElement).value;
              const def = INDICES.find((i) => i.id === id)!;
              const next = { ...state, indexId: id };
              const auto = def.calc ? autoIndexRange(profile, next) : null;
              const patch: Partial<State> = {
                indexId: id, rampId: def.ramp,
                lo: auto ? auto[0] : def.range[0],
                hi: auto ? auto[1] : def.range[1],
              };
              // Classification needs at least one class before it can draw anything,
              // so start one rather than showing a blank layer.
              if (def.sam && !state.classes.length) {
                patch.classes = [{
                  id: `c${Date.now()}`, name: 'Class 1', colour: CLASS_COLOURS[0]!, points: [],
                }];
                patch.activeClass = 0;
              }
              store.set(patch);
            }}>
              {opts.map((o) => <option key={o.id} value={o.id} disabled={o.disabled}>{o.label}</option>)}
            </select>
          </div>
          {state.indexId !== 'sam' && <RampSelect store={store} state={state} />}
          {state.indexId === 'custom' && <CustomExpr store={store} state={state} profile={profile} />}
          {state.indexId === 'sam' && (
            <>
              <div class="row"><label>over</label>
                <select data-testid="underlay" value={state.underlay}
                  onChange={(e) => store.set({ underlay: (e.target as HTMLSelectElement).value })}>
                  <option value="true">true colour</option>
                  <option value="cir">false colour (CIR)</option>
                  <option value="grey">single band (grey)</option>
                  {INDICES.filter((i) => i.calc).map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
              <ClassPanel store={store} state={state} profile={profile} />
            </>
          )}
          {ix && <p class="hint">{ix.about}</p>}
        </>
      )}
      {state.mode === 'single' && (
        <>
          <div class="row"><label>band</label>
            <select data-testid="single-band" value={state.singleBand} onChange={(e) => {
              const i = +(e.target as HTMLSelectElement).value;
              const b = profile.bands[i]!;
              store.set({ singleBand: i,
                rampId: b.role === 'thermal' ? 'magma' : state.rampId,
                lo: physValue(profile, i, b.stats.p2), hi: physValue(profile, i, b.stats.p98) });
            }}>
              {profile.bands.map((b) => <option key={b.index} value={b.index}>{b.index + 1} · {b.role}</option>)}
            </select>
          </div>
          <RampSelect store={store} state={state} />
        </>
      )}
      {state.mode === 'custom' && (['R', 'G', 'B'] as const).map((c, k) => (
        <div class="row" key={c}><label>{c}</label>
          <select value={state.customBands[k]} onChange={(e) => {
            const next = [...state.customBands] as [number, number, number];
            next[k] = +(e.target as HTMLSelectElement).value;
            store.set({ customBands: next });
          }}>
            {profile.bands.map((b) => <option key={b.index} value={b.index}>{b.index + 1} · {b.role}</option>)}
          </select>
        </div>
      ))}
      {state.mode === 'cir' && <p class="hint">NIR, red and green into R, G and B. Healthy vegetation reads bright red.</p>}
      {state.mode === 'rgb' && <p class="hint">Red, green and blue as recorded.</p>}

      <Toggles store={store} state={state} profile={profile} />
      <div class="row"><label>opacity</label>
        <input type="range" data-testid="opacity" min="0" max="1" step="0.02"
          value={state.opacity}
          onInput={(e) => store.set({ opacity: +(e.target as HTMLInputElement).value })} />
      </div>
      <div class="row"><label>scale</label>
        <select data-testid="units" value={state.scaleUnits} onChange={(e) =>
          store.set({ scaleUnits: (e.target as HTMLSelectElement).value as State['scaleUnits'] })}>
          <option value="metric">metric</option><option value="us">US (ft/mi)</option>
          <option value="nautical">nautical</option><option value="degrees">degrees</option>
        </select>
      </div>
      <div class="row"><label>base</label>
        <select data-testid="basemap" value={state.basemap} onChange={(e) =>
          store.set({ basemap: (e.target as HTMLSelectElement).value as State['basemap'] })}>
          <option value="esri">Esri imagery</option><option value="carto">Carto dark</option>
          <option value="osm">OpenStreetMap</option><option value="none">none</option>
        </select>
      </div>
    </>
  );
}

function RampSelect({ store, state }: { store: Store; state: State }) {
  return (
    <div class="row"><label>ramp</label>
      <select data-testid="ramp" value={state.rampId} onChange={(e) =>
        store.set({ rampId: (e.target as HTMLSelectElement).value })}>
        {Object.entries(RAMPS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
      </select>
    </div>
  );
}

function CustomExpr({ store, state, profile }: { store: Store; state: State; profile: Profile }) {
  const { error } = indexExpr(profile, state);
  const names = [...new Set(profile.bands
    .filter((b) => b.role !== 'unassigned' && b.role !== 'alpha').map((b) => b.role))];
  return (
    <div class="custom">
      <textarea data-testid="custom-expr" value={state.customSrc} spellcheck={false}
        onInput={(e) => store.set({ customSrc: (e.target as HTMLTextAreaElement).value })} />
      <div class={error ? 'err' : 'okmsg'}>{error ?? 'valid'}</div>
      <p class="hint">Available: {names.join(', ')}, b1–b{profile.bands.length}</p>
    </div>
  );
}

function Toggles({ store, state, profile }: PanelProps) {
  const chk = (label: string, note: string, checked: boolean, enabled: boolean,
    on: (v: boolean) => void) => (
    <label class={'chk' + (enabled ? '' : ' off')}>
      <input type="checkbox" checked={checked} disabled={!enabled}
        onChange={(e) => on((e.target as HTMLInputElement).checked)} />
      <div>{label}<em>{note}</em></div>
    </label>
  );
  return (
    <>
      {chk('Clip out-of-range values',
        !canClip(state) ? 'Only applies to index and single-band modes.'
          : state.lockRamp ? 'Forced on while the ramp is locked.'
            : 'Pixels outside min/max become transparent instead of clamping.',
        state.clipRange || state.lockRamp, canClip(state) && !state.lockRamp,
        (v) => store.set({ clipRange: v }))}
      {chk('Balance bands independently',
        canBalance(state)
          ? 'Each channel stretched by its own 2–98 range. Off means one shared range, which skews the colour.'
          : 'Only applies to composite modes.',
        state.balanceBands, canBalance(state), (v) => {
          const next = { ...state, balanceBands: v };
          const [lo, hi] = compositeDefaults(profile, next);
          store.set({ balanceBands: v, lo, hi });
        })}
      {chk('Show over true colour',
        !canTrueColour(profile)
          ? 'Needs red, green and blue assigned.'
          : isClassifying(state)
            ? 'Forced on while classifying — training means clicking regions of the image.'
            : 'Draws this file\u2019s own RGB underneath. Use the opacity slider to blend.',
        state.overTrue || isClassifying(state),
        canTrueColour(profile) && !isClassifying(state),
        (v) => store.set({ overTrue: v }))}
      {chk('Discrete classes', 'Management zones instead of a continuous gradient.',
        state.classify, canClip(state), (v) => store.set({ classify: v }))}
      {state.classify && canClip(state) && (
        <div class="row"><label>classes</label>
          <select value={state.classN} onChange={(e) =>
            store.set({ classN: +(e.target as HTMLSelectElement).value })}>
            {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <select value={state.classMethod} onChange={(e) =>
            store.set({ classMethod: (e.target as HTMLSelectElement).value as 'equal' | 'quantile' })}>
            <option value="equal">equal interval</option><option value="quantile">quantile</option>
          </select>
        </div>
      )}
    </>
  );
}

export function StretchPanel({ store, state, profile }: PanelProps) {
  const step = stepFor(state.lo, state.hi);
  const num = (key: 'lo' | 'hi', label: string) => (
    <div class="row"><label>{label}</label>
      <input type="number" step={step} value={snap(state[key], step)}
        data-testid={`stretch-${key}`}
        onInput={(e) => store.set({ [key]: +(e.target as HTMLInputElement).value } as Partial<State>)} />
    </div>
  );
  return (
    <>
      <IndexHistogram store={store} state={state} profile={profile} />
      {canLock(state) && (
        <label class="chk">
          <input type="checkbox" checked={state.lockRamp} onChange={(e) => {
            const on = (e.target as HTMLInputElement).checked;
            store.set(on ? { lockRamp: true, rampLo: state.lo, rampHi: state.hi }
              : { lockRamp: false, rampLo: null, rampHi: null });
          }} />
          <div>Lock colour ramp<em>{state.lockRamp
            ? 'Colours stay pinned to the anchors. Min/max now filters rather than recolours.'
            : 'Off: the ramp follows min/max, so narrowing the range recolours every pixel.'}</em></div>
        </label>
      )}
      {state.lockRamp && state.rampLo != null && (
        <div class="anchored">anchored {fmtNum(state.rampLo)} → {fmtNum(state.rampHi)}
          <button class="btn tiny" onClick={() => store.set({ rampLo: state.lo, rampHi: state.hi })}>
            Re-anchor</button>
        </div>
      )}
      {num('lo', 'min')}{num('hi', 'max')}
      {canBalance(state) && (
        <div class="row"><label>gamma</label>
          <input type="range" min="0.6" max="3.2" step="0.05" value={state.gamma}
            onInput={(e) => store.set({ gamma: +(e.target as HTMLInputElement).value })} />
          <span class="num">{state.gamma.toFixed(2)}</span>
        </div>
      )}
      <div class="btnrow">
        <button class="btn" onClick={() => {
          if (state.mode === 'single') {
            const b = profile.bands[state.singleBand]!;
            const p2 = physValue(profile, b.index, b.stats.p2);
            const p98 = physValue(profile, b.index, b.stats.p98);
            const k = stepFor(p2, p98);
            store.set({ lo: snap(p2, k), hi: snap(p98, k) });
          } else if (canBalance(state)) {
            const [lo, hi] = compositeDefaults(profile, state);
            const k = stepFor(lo, hi);
            store.set({ lo: snap(lo, k), hi: snap(hi, k) });
          } else {
            const st = classificationStats(profile, state);
            if (st?.p2 != null) {
              const k = stepFor(st.p2, st.p98!);
              store.set({ lo: snap(st.p2, k), hi: snap(st.p98!, k) });
            }
          }
        }}>Auto (p2–p98)</button>
        <button class="btn" title="Reset to the index's full theoretical range"
          onClick={() => {
          const ix = INDICES.find((i) => i.id === state.indexId);
          if (state.mode === 'index' && ix) store.set({ lo: ix.range[0], hi: ix.range[1] });
          else if (canBalance(state)) {
            const [lo, hi] = compositeDefaults(profile, state);
            store.set({ lo, hi });
          }
        }}>Index range</button>
      </div>
    </>
  );
}

export function BandsPanel({ store, state, profile }: PanelProps) {
  const guessed = profile.bands.filter((b) => b.source === 'guess').length;
  const placed = profile.bands.map((b, i) => ({ b, i }))
    .filter((x) => x.b.wavelengthNm != null)
    .sort((x, y) => x.b.wavelengthNm! - y.b.wavelengthNm!);
  return (
    <>
      {/* Band order in a file is arbitrary; wavelength position is physical. */}
      <div class="axis">
        <div class="axis-bar" style={{ background: spectrumGradient() }} />
        {[500, 700, 1000, 1600, 2200].map((nm) => (
          <span class="axis-tick" key={nm} style={{ left: axPos(nm) * 100 + '%' }}>{nm}</span>
        ))}
        {placed.map(({ b, i }) => {
          const [r, g, bl] = wavelengthRGB(b.wavelengthNm!);
          const bright = Math.max(r, g, bl) < 110
            ? `rgb(${r + 90},${g + 70},${bl + 70})` : `rgb(${r},${g},${bl})`;
          return <span class="mk" key={i} title={`Band ${i + 1} → ${b.role} · ${b.wavelengthNm}nm`}
            style={{ left: axPos(b.wavelengthNm!) * 100 + '%', color: bright }}>{i + 1}</span>;
        })}
      </div>

      {guessed > 0 && <div class="chip">{guessed} band{guessed > 1 ? 's' : ''} still guessed — confirm below</div>}

      {profile.bands.map((b) => (
        <div class="bandbox" key={b.index}>
          <div class="brow">
            <BandThumb profile={profile} band={b} />
            <div class="bsel">
              <div class="bn"><span>band {b.index + 1}</span>
                <span class={'tag ' + b.source} title={b.why}>{b.source}</span></div>
              <select data-testid={`band-${b.index}`} value={b.role} onChange={(e) => {
                const role = (e.target as HTMLSelectElement).value as BandRole;
                store.setBandRole(b.index, role, NM[role] ?? null);
              }}>
                {ROLES.map((r) => <option key={r} value={r}>{NM[r] ? `${r} · ${NM[r]}nm` : r}</option>)}
              </select>
            </div>
          </div>
          <BandHistogram band={b} />
          <BandStatsRow profile={profile} band={b} />
          {b.description && <div class="desc">{b.description}</div>}
          {b.stats.validFraction < 0.999 &&
            <div class="nodata">{(100 - b.stats.validFraction * 100).toFixed(1)}% nodata</div>}
        </div>
      ))}
      {[...profile.bandNotes, ...profile.warnings].map((w, i) => (
        <div class={'note ' + w.level} key={i}>{w.text.replace(/\*\*/g, '')}</div>
      ))}
      <p class="hint">Confidence: <b>metadata</b> the file said so · <b>inferred</b> from
        structure · <b>guess</b> from band count · <b>manual</b> yours.</p>
      <input type="hidden" value={state.mode} />
    </>
  );
}

function spectrumGradient(): string {
  const stops: string[] = [];
  for (let nm = 400; nm <= 2400; nm *= 1.04) {
    const [r, g, b] = wavelengthRGB(nm);
    stops.push(`rgb(${r},${g},${b}) ${(axPos(nm) * 100).toFixed(1)}%`);
  }
  return `linear-gradient(to right,${stops.join(',')})`;
}
